// src/components/common/MobileFormViewManager.jsx
// 모바일 전용 FormViewManager (웹사이트 FormViewManager와 분리)
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MobileShareButton from "./MobileShareButton";
import FileList from "../papers/FileList";
import ApprovalLine_view from "../../approval/ApprovalLine_view";
import ApprovalAction from "../../approval/ApprovalAction";
import * as designMeta from "../../../api/design/meta";
import * as approvalMeta from "../../../api/approval/meta";
import api from "../../../api/axios";
import { viewApprovalLine } from "../../../api/approval";
import { getSafetyDocDetail } from "../../../api/safety";
import PaperShell from "../layout/PaperShell";
import TableCell from "../ui/TableCell";
import MobileApprovalLine from "../../approval/MobileApprovalLine";
import {
  getWorkReportDetail,
  getProgressDetail,
  getWorkOrderDetail,
  getCompletionReportDetail,
} from "../../../api/const";
import { getProjectName } from "../../../api/project";
import { softDeleteDoc } from "../../../api/papers";
import ConfirmDialog from "../ui/ConfirmModal";
import { getQualityMeta, deleteQualityMeta } from "../../../api/quality/meta";
import { getMaterialDoc } from "../../../api/material/docs";

export default function MobileFormViewManager({ params, children, doc_name }) {
  const navigate = useNavigate();
  const { project, dns, module, doc_type } = params || {};

  const [projectName, setProjectName] = useState("");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleDelete = async () => {
    try {
      // 품질 모듈은 deleteQualityMeta 사용 (PC 버전과 동일)
      if (module === "quality") {
        await deleteQualityMeta({ dns });
      } else {
        await softDeleteDoc(dns, true);
      }
      alert("문서가 삭제되었습니다.");
      navigate(-1);
    } catch (e) {
      console.error(e);
      alert("삭제 실패");
    }
  };

  useEffect(() => {
    const load = async () => {
      const res = await getProjectName(project);
      setProjectName(res.name);
    };
    load();
  }, [project]);

  const isDaily = module === "const" && doc_type === "DAILY";
  const isWeekly = module === "const" && doc_type === "WEEKLY";

  const [data, setData] = useState(null);
  const [approvalLines, setApprovalLines] = useState([]);
  const [loading, setLoading] = useState(false);

  const me = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    if (!dns) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        let res;

        if (module === "design") {
          res = await designMeta.getMeta({ dns });
        } else if (module === "quality") {
          res = await getQualityMeta({ dns });
        } else if (module === "approval") {
          res = await approvalMeta.getMeta(dns);
        } else if (module === "safety") {
          const safetyRes = await getSafetyDocDetail(dns);
          res = { data: safetyRes };
        } else if (module === "const") {
          if (doc_type === "DAILY") {
            const data = await getWorkReportDetail(dns);
            res = { data };
          } else if (doc_type === "weekly" || doc_type === "progress") {
            const data = await getProgressDetail(dns);
            res = { data };
          } else if (doc_type === "workorder") {
            const data = await getWorkOrderDetail(dns);
            res = { data };
          } else if (doc_type === "ordercomplete") {
            const data = await getCompletionReportDetail(dns);
            res = { data };
          } else {
            throw new Error("지원되지 않는 const 문서 타입입니다.");
          }
        } else if (module === "materials") {
          const doc = await getMaterialDoc({ dns, project });
          res = { data: doc };
        } else {
          throw new Error("지원되지 않는 모듈입니다.");
        }

        setData(res.data || res);

        try {
          const approval = await viewApprovalLine(dns);
          setApprovalLines(approval.data.approval_lines || []);
        } catch {
          setApprovalLines([]);
        }
      } catch (err) {
        console.error("MobileFormViewManager fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dns, module, project]);

  const getCreatedBy = useMemo(() => {
    if (!data) return null;
    if (data.created_by) return data.created_by;
    if (data.doc?.created_by) return data.doc.created_by;
    if (data.created_by_id) return { id: data.created_by_id };
    return null;
  }, [data]);

  const isOwner = useMemo(() => {
    if (!getCreatedBy?.id || !me?.id) return false;
    return String(getCreatedBy.id) === String(me.id);
  }, [getCreatedBy, me]);
  const ALLOW_EDIT_STATES = [undefined, null, "", "none", "submit"];
  const ALLOW_EDIT_STATE_VALUES = ["save"]; // design 모듈의 state 값

  const canEdit = useMemo(() => {
    // status 또는 state 필드 확인 (design 모듈은 state 사용)
    const s = data?.status || data?.meta?.status || data?.state || data?.meta?.state;
    // status가 허용된 상태이거나, state가 "save"인 경우 수정 가능
    return isOwner && (ALLOW_EDIT_STATES.includes(s) || ALLOW_EDIT_STATE_VALUES.includes(s));
  }, [data, isOwner]);

  if (loading)
    return (
      <div className="text-gray-400 text-center py-8">
        불러오는 중...
      </div>
    );

  if (!data)
    return (
      <div className="text-center text-gray-400 py-8">
        데이터를 불러올 수 없습니다.
      </div>
    );

  const meta = data.meta || {};

  return (
    <div className="w-full">
      <div className="w-full">
        {/* 📄 본문 (모바일 최적화) */}
        <div className="w-full min-w-0">
          <div className="no-print flex items-center justify-between mb-4 py-3">
            {/* 왼쪽: 목록으로 */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              <ArrowLeft size={16} /> 목록으로
            </button>

            {/* 오른쪽: 수정 / 삭제 / 인쇄 */}
            <div className="flex items-center gap-2 flex-shrink-0" style={{ flexShrink: 0 }}>
              {canEdit && (
                <>
                  <button
                    onClick={() => {
                      const editUrl = window.location.pathname.replace(
                        /\/view$/,
                        ""
                      );
                      navigate(editUrl);
                    }}
                    className="btn-edit text-xs px-2.5 py-1.5"
                  >
                    수정
                  </button>

                  {module === "design" ? (
                    data.state === "save" && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="btn-delete text-xs px-2.5 py-1.5"
                        style={{ fontSize: '0.75rem', paddingLeft: '0.625rem', paddingRight: '0.625rem', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                      >
                        삭제
                      </button>
                    )
                  ) : (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="btn-delete text-xs px-2.5 py-1.5"
                      style={{ fontSize: '0.75rem', paddingLeft: '0.625rem', paddingRight: '0.625rem', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                    >
                      삭제
                    </button>
                  )}
                </>
              )}
              <MobileShareButton
                title={data?.title || doc_name || "문서"}
                contentId="pdf-content"
              />
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
              >
                <Printer size={14} /> 인쇄
              </button>
            </div>
          </div>

          {/* 🧾 문서 본문 (모바일 최적화) */}
          <div className="mobile-paper-wrapper w-full">
            <style>{`
              @media screen {
                .mobile-paper-wrapper {
                  width: 100% !important;
                  max-width: 100% !important;
                  min-width: 0 !important;
                  overflow-x: hidden !important;
                  box-sizing: border-box !important;
                }
                .mobile-paper-wrapper .paper {
                  width: 100% !important;
                  max-width: 100% !important;
                  min-width: 0 !important;
                  margin: 0 !important;
                  box-sizing: border-box !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                .mobile-paper-wrapper .paper-inner {
                  padding: 1rem !important;
                  box-sizing: border-box !important;
                  width: 100% !important;
                  max-width: 100% !important;
                  min-width: 0 !important;
                  overflow-x: hidden !important;
                }
                .mobile-paper-wrapper .meta-table,
                .mobile-paper-wrapper .items-table,
                .mobile-paper-wrapper .sign-grid,
                .mobile-paper-wrapper table {
                  width: 100% !important;
                  max-width: 100% !important;
                  min-width: 0 !important;
                  table-layout: auto !important;
                  font-size: 12px !important;
                  border-collapse: collapse !important;
                }
                .mobile-paper-wrapper .meta-table th,
                .mobile-paper-wrapper .meta-table td,
                .mobile-paper-wrapper .items-table th,
                .mobile-paper-wrapper .items-table td,
                .mobile-paper-wrapper table th,
                .mobile-paper-wrapper table td {
                  padding: 4px 6px !important;
                  word-break: break-word !important;
                  overflow-wrap: break-word !important;
                  max-width: 100% !important;
                  box-sizing: border-box !important;
                }
                .mobile-paper-wrapper .meta-table th,
                .mobile-paper-wrapper table th {
                  width: auto !important;
                  min-width: 80px !important;
                  max-width: 30% !important;
                }
                .mobile-paper-wrapper .meta-table td,
                .mobile-paper-wrapper table td {
                  width: auto !important;
                  min-width: 0 !important;
                }
                .mobile-paper-wrapper colgroup,
                .mobile-paper-wrapper col {
                  width: auto !important;
                  min-width: 0 !important;
                }
                .mobile-paper-wrapper .doc-header,
                .mobile-paper-wrapper header,
                .mobile-paper-wrapper section,
                .mobile-paper-wrapper div {
                  max-width: 100% !important;
                  box-sizing: border-box !important;
                  overflow-x: hidden !important;
                }
                .mobile-paper-wrapper .prose,
                .mobile-paper-wrapper .prose * {
                  max-width: 100% !important;
                  box-sizing: border-box !important;
                  word-break: break-word !important;
                  overflow-wrap: break-word !important;
                }
                .mobile-paper-wrapper img {
                  max-width: 100% !important;
                  height: auto !important;
                  box-sizing: border-box !important;
                }
              }
            `}</style>

            {/* PDF 다운로드용 콘텐츠 영역 */}
            <div id="pdf-content">
              {/* 결재이력 - PaperShell 외부에서 렌더링하여 overflow 제한 회피 */}
              {approvalLines.length > 0 && (
                <div className="mb-4">
                  <MobileApprovalLine lines={approvalLines} />
                </div>
              )}

              <PaperShell>
                {/* 헤더 */}
                <header className="doc-header pb-5">

                  {/* 문서 헤더 카드 */}
                  <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-4 mb-4">
                    {/* 문서 타입 뱃지 + 제목 */}
                    <div className="mb-3">
                      <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 mb-2">
                        {isDaily ? "공사일보" : doc_name || `${doc_type} 문서`}
                      </span>
                      <h1 className="text-lg font-bold text-slate-900 leading-snug break-words">
                        {data.title || projectName || "-"}
                      </h1>
                    </div>

                    {/* DNS */}
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 pb-3 border-b border-slate-200">
                      <span className="font-medium text-slate-400">DNS</span>
                      <span className="font-mono text-slate-600 break-all">{data.doc_dns || dns || "-"}</span>
                    </div>

                    {/* 메타 정보 그리드 */}
                    {isDaily ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">현장명</p>
                          <p className="text-sm text-slate-800 font-medium">{data.title || projectName || "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">일자</p>
                          <p className="text-sm text-slate-800 font-medium">{meta.date || "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">작성자</p>
                          <p className="text-sm text-slate-800 font-medium">{data.created_by?.name || data.created_by || "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">확인자</p>
                          <p className="text-sm text-slate-800 font-medium">{data.confirmer || "-"}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <p className="text-xs text-slate-400 mb-0.5">현장명</p>
                          <p className="text-sm text-slate-800 font-medium">{data.project_name || projectName || project || "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">작성일</p>
                          <p className="text-sm text-slate-800 font-medium">
                            {data.created_at
                              ? new Date(data.created_at).toLocaleDateString("ko-KR", { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '')
                              : meta.create_date
                                ? new Date(meta.create_date).toLocaleDateString("ko-KR", { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '')
                                : "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">작성자</p>
                          <p className="text-sm text-slate-800 font-medium">
                            {data.created_by?.name ||
                              data.created_by_name ||
                              data.created_by ||
                              "-"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </header>

                {/* 🧩 children에서 문서 내용 표시 */}
                <div className="w-full">
                  {React.Children.map(children, (child) =>
                    React.isValidElement(child)
                      ? React.cloneElement(child, { meta, data, project })
                      : child
                  )}
                </div>

                {/* 첨부자료 */}
                <h2 className="font-semibold text-gray-700 mt-6 mb-2 no-print">
                  첨부자료
                </h2>
                <div className="border rounded-xl p-3 no-print">
                  <FileList dns={dns} />
                </div>

                <div className="text-xs text-gray-400 mt-6 text-center">
                  ※ 본 문서는 시스템에서 자동 생성된 전자문서 원본입니다.
                </div>
              </PaperShell>
            </div>
          </div>
        </div>

        {/* ✅ 결재 패널 (모바일에서는 하단에 표시) */}
        {approvalLines.length > 0 && (
          <div className="no-print mt-4">
            <div className="border p-2 rounded-lg">
              <ApprovalLine_view lines={approvalLines} />
            </div>
            <div className="mt-2">
              <ApprovalAction approvalLines={approvalLines} />
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="문서 삭제"
        description="정말 이 문서를 삭제하시겠습니까? 삭제 후 되돌릴 수 없습니다."
        confirmText="삭제"
        cancelText="취소"
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDelete();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

